import type { Timestamp } from 'firebase/firestore'
import type { WorkspaceRole } from './workspace'
import type { AccessScope } from './schema'

export type PendingInviteType = 'EMAIL' | 'PHONE' | 'LINK'
export type PendingInviteStatus = 'PENDING' | 'CLAIMED' | 'REVOKED' | 'DECLINED' | 'EXPIRED'

/** Sentinel recipient for open LINK invites — any signed-in user can claim. */
export const OPEN_INVITE_RECIPIENT = 'OPEN'

export type PendingInviteRole = WorkspaceRole | 'MEMBER'
export type WebPendingInviteRole = Exclude<PendingInviteRole, 'HELPER'>

export const isWebPendingInviteRole = (role: unknown): role is WebPendingInviteRole =>
  role === 'ADMIN' || role === 'EDITOR' || role === 'VIEWER' || role === 'READONLY' || role === 'MEMBER'

/**
 * Unified invitation record. Source of truth for every outgoing invite (email,
 * phone, link). Claimed entries are kept (status=CLAIMED) so audit + analytics
 * can correlate the claim back to the original invite.
 */
export interface PendingInvite {
  id: string
  type: PendingInviteType
  /** Email (lowercased), E.164 phone, or OPEN_INVITE_RECIPIENT for LINK invites. */
  recipient: string
  /** Unique token — for LINK invites this is the join token; for EMAIL/PHONE it's a random id. */
  token: string
  role: PendingInviteRole
  workspaceId: string
  workspaceName: string
  workspaceSlug?: string | null
  status: PendingInviteStatus
  claimedBy: string | null
  invitedBy: string
  invitedByName?: string | null
  notifiedUserId?: string | null
  createdAt: Timestamp | null
  claimedAt?: Timestamp | null
  revokedAt?: Timestamp | null
  declinedAt?: Timestamp | null
  /** Optional soft-expiry (ms epoch). Null = no expiry. */
  expiresAt?: number | null
  /** Optional legacy membership doc id if migrated by Phase 2 script */
  migratedFromMembershipId?: string | null
  /** Optional transition history for tracking resends and lifecycle changes */
  inviteStatusHistory?: InviteStatusHistoryEntry[]
  /** Optional scoped access configuration for the invite. */
  accessScope?: AccessScope
}

export interface InviteStatusHistoryEntry {
  status: PendingInviteStatus
  changedAt: number
  reason?: string
}

export type PendingInviteFilter = 'ALL' | 'EMAIL' | 'PHONE' | 'LINK'

export interface WebPendingInvite extends PendingInvite {
  role: WebPendingInviteRole
}

export const isWebPendingInvite = (invite: PendingInvite): invite is WebPendingInvite =>
  invite.role !== 'HELPER' && isWebPendingInviteRole(invite.role)
